import { useState } from "react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Link } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Phone } from "lucide-react";

const galleryImages = [
  {
    id: 1,
    src: "https://images.unsplash.com/photo-1770169096244-90f4288392d7?auto=format&fit=crop&w=800&q=80",
    title: "Lush Garden Paradise",
    category: "Garden Design"
  },
  {
    id: 2,
    src: "https://images.unsplash.com/photo-1759355787109-fd0df9c47e2e?auto=format&fit=crop&w=800&q=80",
    title: "Charming Home Garden",
    category: "Garden Design"
  },
  {
    id: 3,
    src: "https://images.pexels.com/photos/47313/tulips-bloom-blossom-colorful-47313.jpeg?auto=compress&w=800",
    title: "Colorful Tulip Beds",
    category: "Flower Gardens"
  },
  {
    id: 4,
    src: "https://images.unsplash.com/photo-1772040942277-b194d9d0b648?auto=format&fit=crop&w=800&q=80",
    title: "Luxury Outdoor Living",
    category: "Landscaping"
  },
  {
    id: 5,
    src: "https://images.unsplash.com/photo-1764296377273-4299e3f3d6f5?auto=format&fit=crop&w=800&q=80",
    title: "Pristine Manicured Lawn",
    category: "Lawn Care"
  },
  {
    id: 6,
    src: "https://images.unsplash.com/photo-1701750204317-213f30c7fd6d?auto=format&fit=crop&w=800&q=80",
    title: "Marigold Garden Beauty",
    category: "Flower Gardens"
  },
  {
    id: 7,
    src: "https://images.unsplash.com/photo-1558904541-efa843a96f01?auto=format&fit=crop&w=800&q=80",
    title: "Perfect Green Lawn",
    category: "Lawn Care"
  },
  {
    id: 8,
    src: "https://images.unsplash.com/photo-1630311853242-edd0c07766c0?auto=format&fit=crop&w=800&q=80",
    title: "Professional Mowing",
    category: "Lawn Care"
  },
  {
    id: 9,
    src: "https://images.unsplash.com/photo-1631752674874-9994662712af?auto=format&fit=crop&w=800&q=80",
    title: "Garden Bed Design",
    category: "Garden Design"
  },
  {
    id: 10,
    src: "https://images.unsplash.com/photo-1597257399185-d4f2ec93edca?auto=format&fit=crop&w=800&q=80",
    title: "Beautiful Blue Flowers",
    category: "Flower Gardens"
  },
  {
    id: 11,
    src: "https://images.unsplash.com/photo-1691114885785-cfdab4d7c3a0?auto=format&fit=crop&w=800&q=80",
    title: "Rose Garden Landscape",
    category: "Flower Gardens"
  },
  {
    id: 12,
    src: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?auto=format&fit=crop&w=800&q=80",
    title: "Vibrant Garden Path",
    category: "Landscaping"
  },
  {
    id: 13,
    src: "https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?auto=format&fit=crop&w=800&q=80",
    title: "Fresh Mulch Installation",
    category: "Landscaping"
  },
  {
    id: 14,
    src: "https://images.unsplash.com/photo-1544914379-806667cd9489?auto=format&fit=crop&w=800&q=80",
    title: "Lush Green Grass",
    category: "Lawn Care"
  },
  {
    id: 15,
    src: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80",
    title: "Curb Appeal Transformation",
    category: "Full Service"
  },
  {
    id: 16,
    src: "https://images.pexels.com/photos/5678734/pexels-photo-5678734.jpeg?auto=compress&w=800",
    title: "Colorful Garden Display",
    category: "Flower Gardens"
  }
];

const categories = ["All", "Lawn Care", "Garden Design", "Flower Gardens", "Landscaping", "Full Service"];

export const GalleryPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedImage, setSelectedImage] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const filteredImages = selectedCategory === "All" 
    ? galleryImages 
    : galleryImages.filter(img => img.category === selectedCategory);

  const openLightbox = (image) => {
    setSelectedImage(image);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedImage(null);
  };

  const navigateImage = (direction) => {
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    let newIndex;
    if (direction === "next") {
      newIndex = (currentIndex + 1) % filteredImages.length;
    } else {
      newIndex = (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    }
    setSelectedImage(filteredImages[newIndex]);
  };

  return (
    <div data-testid="gallery-page">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-b from-emerald-50 to-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary via-emerald-400 to-primary"></div>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-primary/10 text-primary font-accent text-sm rounded-full mb-4">OUR PORTFOLIO</span>
            <h1 className="text-5xl md:text-6xl font-heading text-gray-900 mb-4">
              Our <span className="text-primary">Work</span>
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Browse our portfolio of stunning gardens, beautiful lawns, and complete property transformations. 
              See the Elite Blade difference.
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                variant={selectedCategory === category ? "default" : "outline"}
                onClick={() => setSelectedCategory(category)}
                className={`font-accent ${
                  selectedCategory === category 
                    ? "bg-primary text-white hover:bg-primary/90 shadow-lg" 
                    : "border-2 border-gray-300 text-gray-700 hover:border-primary hover:text-primary"
                }`}
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredImages.map((image) => (
              <div
                key={image.id}
                data-testid={`gallery-item-${image.id}`}
                className="group relative aspect-square overflow-hidden rounded-2xl border-4 border-white shadow-lg cursor-pointer hover:shadow-2xl transition-all duration-300"
                onClick={() => openLightbox(image)}
              >
                <img
                  src={image.src}
                  alt={image.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-white font-heading text-lg drop-shadow-lg">{image.title}</p>
                    <p className="text-primary text-sm font-accent">{image.category}</p>
                  </div>
                </div>
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Lightbox */}
          <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
            <DialogContent className="max-w-5xl bg-white border-gray-200 p-0 rounded-2xl overflow-hidden">
              {selectedImage && (
                <div className="relative">
                  <button
                    data-testid="lightbox-close"
                    onClick={closeLightbox}
                    className="absolute top-4 right-4 z-10 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                  <button
                    data-testid="lightbox-prev"
                    onClick={() => navigateImage("prev")}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    data-testid="lightbox-next"
                    onClick={() => navigateImage("next")}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                  <img
                    src={selectedImage.src}
                    alt={selectedImage.title}
                    className="w-full h-auto max-h-[80vh] object-contain"
                  />
                  <div className="p-6 text-center bg-gradient-to-t from-gray-100 to-white">
                    <h3 className="text-2xl font-heading text-gray-900">{selectedImage.title}</h3>
                    <p className="text-primary font-accent text-sm mt-1">{selectedImage.category}</p>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-primary via-emerald-500 to-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.pexels.com/photos/47313/tulips-bloom-blossom-colorful-47313.jpeg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl font-heading text-white mb-6 drop-shadow-lg">
            Want Results Like These?
          </h2>
          <p className="text-white/90 mb-8 text-lg drop-shadow-md">
            Let us transform your property into the outdoor space of your dreams. 
            Contact us today for a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                data-testid="gallery-quote-btn"
                size="lg"
                className="bg-accent hover:bg-accent/90 text-black font-accent text-lg px-8 shadow-xl"
              >
                Get Your Free Quote
              </Button>
            </Link>
            <a href="tel:7134999372">
              <Button 
                data-testid="gallery-call-btn"
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-primary font-accent text-lg px-8"
              >
                <Phone className="mr-2 h-5 w-5" />
                (713) 499-9372
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
